import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  resolveFocusedEngagement,
  writeFocusedEngagementPreference,
  type FocusableEngagement,
  type FocusResolution,
} from '../utils/focusedEngagement';

export interface UseFocusedEngagementArgs<T extends FocusableEngagement> {
  /** All engagements the user may access on this company. */
  engagements: ReadonlyArray<T> | null | undefined;
  companyId: string | null | undefined;
  userId: string | null | undefined;
}

export interface UseFocusedEngagementResult<T extends FocusableEngagement> {
  focusedEngagementId: string | null;
  focusedEngagement: T | null;
  /** Select an engagement and persist it as this user's preference for the company. */
  setFocusedEngagement: (engagementId: string | null) => void;
  /** True when the company has two or more engagements (show a switcher). */
  hasMultiple: boolean;
  resolution: FocusResolution;
}

/**
 * React binding for the PRD §4.7 focused-engagement rule.
 *
 * Give it the company's engagement list; it returns the one in focus, honouring
 * the URL, then the stored per-user preference, then the standard fallbacks.
 * Calling `setFocusedEngagement` stores the choice so it survives reloads.
 */
export function useFocusedEngagement<T extends FocusableEngagement>({
  engagements,
  companyId,
  userId,
}: UseFocusedEngagementArgs<T>): UseFocusedEngagementResult<T> {
  const [focusedEngagementId, setFocusedEngagementId] = useState<string | null>(null);
  const [resolution, setResolution] = useState<FocusResolution>('none');

  const list = useMemo(() => (engagements ?? []).filter(Boolean) as T[], [engagements]);

  useEffect(() => {
    // Keep a still-valid selection; otherwise re-resolve from the rules.
    if (focusedEngagementId && list.some((e) => e.id === focusedEngagementId)) return;
    const pick = resolveFocusedEngagement(list, { userId, companyId });
    setFocusedEngagementId(pick.engagement?.id ?? null);
    setResolution(pick.resolution);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list, companyId, userId]);

  const setFocusedEngagement = useCallback(
    (engagementId: string | null) => {
      setFocusedEngagementId(engagementId);
      setResolution(engagementId ? 'preference' : 'none');
      writeFocusedEngagementPreference(userId, companyId, engagementId);
    },
    [userId, companyId],
  );

  const focusedEngagement = useMemo(
    () => (focusedEngagementId ? list.find((e) => e.id === focusedEngagementId) ?? null : null),
    [list, focusedEngagementId],
  );

  return {
    focusedEngagementId,
    focusedEngagement,
    setFocusedEngagement,
    hasMultiple: list.length > 1,
    resolution,
  };
}
